/* eslint-disable @typescript-eslint/no-misused-promises */
/**
 * Sign up Form
 */

import {
  Box,
  Button,
  Heading,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";

import { yupResolver } from "@hookform/resolvers/yup";
import { CheckboxField, TextField } from "components";
import { SignUpSchema } from "config/schema/SignUpSchema";
import { ModalTypes } from "config/types/modals";
import useAuth from "hooks/useAuth";
import { useSignUpQuery } from "_actions/AuthActions";
import { SecondaryButton } from "components/base/atoms/HJSButton";
import { useForm } from "react-hook-form";
import { setFormErrors } from "utils";

const initialFormValues: ISignUpValues = {
  firstName: "",
  email: "",
  password: "",
  isAdult: false,
  isUSResident: false,
};

interface ISignUpValues {
  firstName: string;
  email: string;
  password: string;
  isAdult: boolean | undefined;
  isUSResident: boolean | undefined;
}
interface IModalComponentProps {
  changeModalType: (modalType: ModalTypes) => void;
}
type TModalComponent = (props: IModalComponentProps) => JSX.Element;

type THandleChangeFields = "isAdult" | "isUSResident";

const SignupForm: TModalComponent = ({
  changeModalType,
}: IModalComponentProps) => {
  const { setAuthState } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
  } = useForm<ISignUpValues>({
    resolver: yupResolver(SignUpSchema),
    defaultValues: initialFormValues,
  });

  const [isAdult, isUSResident] = watch(["isAdult", "isUSResident"]);
  const { mutate: signUpUser, isLoading } = useSignUpQuery();
  const { setAuthToken } = useAuth();

  const submitHandler: (values: ISignUpValues) => void = (
    values: ISignUpValues
  ) => {
    signUpUser(
      {
        email: values.email,
        password: values.password,
        name: values.firstName,
      },
      {
        onSuccess: (resp: any) => {
          changeModalType(ModalTypes.VERIFICATION_CODE);
          // setTimeout(() => {
          setAuthState({ email: values.email });
          setAuthToken(resp?.token);
          // }, 500);
        },
        onError: (error) => {
          setFormErrors(error, setError);
        },
      }
    );
  };

  const handleChange: (field: THandleChangeFields, value: boolean) => void = (
    field: THandleChangeFields,
    value: boolean
  ) => {
    setValue(field, value, {
      shouldValidate: Boolean(field in errors),
    });
  };
  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <ModalContent
        px={{ base: 0, md: 6 }}
        py={4}
        style={{ maxHeight: "90vh", overflow: "auto" }}
      >
        <ModalHeader>
          <VStack align="flex-start">
            <Heading as="h2" fontSize={[20, 20, 30, 40]} color="teal.900">
              Start earning today!
            </Heading>
            <Text color="hjsBlack" fontWeight="500" fontSize={[12, 12, 18, 24]}>
              Already a user?&nbsp;
              <Button
                size="xl"
                fontWeight="500"
                color="hjsBlack"
                variant="link"
                onClick={() => changeModalType(ModalTypes.LOGIN)}
                className="!underline"
              >
                Sign in
              </Button>{" "}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack mt={4} alignItems="flex-start" gap={3}>
            <TextField
              label="First Name"
              placeholder="Enter your name"
              error={Boolean(errors.firstName)}
              helperText={errors.firstName?.message}
              {...register("firstName")}
            />

            <TextField
              label="Email"
              placeholder="Enter your email"
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              {...register("email")}
            />

            <TextField
              label="Password"
              placeholder="Enter your password"
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              {...register("password")}
              type="password"
            />

            <Box>
              <Stack
                mt={6}
                direction={{
                  base: "column",
                  md: "row",
                }}
                alignItems="center"
              >
                <CheckboxField
                  label="I am 18 or older"
                  error={Boolean(errors.isAdult)}
                  helperText={errors.isAdult?.message}
                  isChecked={isAdult}
                  onChange={(event) =>
                    handleChange("isAdult", event.target.checked)
                  }
                />

                <CheckboxField
                  label="I am a resident of the United States"
                  error={Boolean(errors.isUSResident)}
                  helperText={errors.isUSResident?.message}
                  isChecked={isUSResident}
                  onChange={(event) =>
                    handleChange("isUSResident", event.target.checked)
                  }
                />
              </Stack>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter alignItems="center" justifyContent="center">
          <VStack mt={4} spacing={4}>
            <SecondaryButton
              onClick={handleSubmit(submitHandler)}
              isLoading={isLoading}
              type="submit"
              px={16}
            >
              Sign up
            </SecondaryButton>
            <Button
              fontWeight="400"
              color="hjsBlack"
              variant="link"
              fontSize={[14, 14, 16, 18]}
              onClick={() => changeModalType(ModalTypes.PRIVACY_POLICY)}
            >
              Privacy Policy
            </Button>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </form>
  );
};

export default SignupForm;
